import { classNames } from '@vkontakte/vkui';
import React, { type ReactElement, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SHARING_VARIANT } from 'reactApp/components/SharingWindow/blocks/Weblink/WeblinkList/SharingNewWeblinkList.types';
import { AB_FEATURE_NAME, TOOLTIP_OFFSET } from 'reactApp/components/SharingWindow/Sharing.constants';
import { sendGa } from 'reactApp/components/SharingWindow/Sharing.helpers';
import { type IAccessRightsOptions, DomainAccess, EAccessRights } from 'reactApp/components/SharingWindow/Sharing.types';
import { SharingDropdown } from 'reactApp/components/SharingWindow/ui/Dropdown/SharingDropdown';
import { EDropdownColors } from 'reactApp/components/SharingWindow/ui/Dropdown/SharingDropdown.types';
import { deleteUser, setAccessRight } from 'reactApp/modules/folderAccessControlList/folderAccessControlList.actions';
import { XrayItemType } from 'reactApp/modules/home/home.types';
import { unmountRequest } from 'reactApp/modules/modifying/modifying.actions';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getSearchRequestParams } from 'reactApp/modules/search/search.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { weblinkDeleteEmailAccess, weblinkSetEmailAccess } from 'reactApp/modules/weblinkAccessControl/weblinkAccessControl.actions';
import { EPublicAccessRights } from 'reactApp/modules/weblinkAccessControl/weblinkAccessControl.types';
import { Dropdown } from 'reactApp/ui/Dropdown/Dropdown';
import { DropdownMenu } from 'reactApp/ui/Dropdown/DropdownMenu';
import { tooltipPlacements } from 'reactApp/ui/Tooltip/Tooltip.constants';
import { sendDwh, sendXray } from 'reactApp/utils/ga';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import {
    DELETE_USER,
    getAccessRightsOptions,
    getPublicAccessRightsOptions,
    PUBLIC_ACCESS_RIGHTS_OPTIONS_FOLDER_BIZ,
    UNMOUNT_USER,
} from './User.constants';
import styles from './User.css';
import { renderDeletePublicAccessDialog, renderDeleteUserDialog } from './User.helpers';

const useDesktopDropdownColor = (variant: SHARING_VARIANT): EDropdownColors => {
    return variant === SHARING_VARIANT.default ? EDropdownColors.GRAY : EDropdownColors.BLUE;
};

// eslint-disable-next-line max-lines-per-function
export const useAccessRights = ({ user, item, isMounted, onClose, isPhone, isOwner, publicId, variant }): ReactElement | null | string => {
    const dispatch = useDispatch();

    const accessRightsOptions = getAccessRightsOptions();
    const userRight = accessRightsOptions[user.accessRights];
    const storage = useSelector(getCurrentStorage);
    const { xPageId, xReqId, query } = useSelector(getSearchRequestParams);
    const isBizUser = useSelector(UserSelectors.isBizUser);

    const onActionClick = useCallback(() => {
        if (!user || !item) {
            return;
        }
        sendGa('delete-user');

        const xrayUserType = isBizUser ? 'biz' : 'b2c';

        sendDwh({
            eventCategory: ECategoryGa.sharedFolder,
            action: 'delete-user',
            label: xrayUserType,
        });

        sendPaymentGa({
            eventCategory: publicId ? ECategoryGa.public : ECategoryGa.sharedFolder,
            action: 'shutdown-sharing',
            public_id: publicId,
        });

        if (storage === EStorageType.search) {
            sendPaymentGa({
                eventCategory: ECategoryGa.publicSearch,
                action: 'shutdown-sharing',
                public_id: publicId,
                section: storage,
                search_phrase: query,
                name_files: item.name,
                type_content: 'folder',
                page_id: xPageId,
                req_id: xReqId,
                pos: item.pos,
            });
        }

        dispatch(deleteUser({ email: user.email, item }));
    }, [user, item, publicId, storage, dispatch, query, xPageId, xReqId]);

    const handleAccessRightsUpdate = useCallback(
        (accessRight): void => {
            if (!user) {
                return;
            }

            if (accessRight === DELETE_USER.key) {
                renderDeleteUserDialog({ user, onActionClick, variant });
                return;
            }

            sendGa('user-rights-change');
            dispatch(setAccessRight({ email: user.email, accessRight, folderId: item?.id }));
        },
        [dispatch, item?.id, onActionClick, user, variant]
    );

    const onUnmount = useCallback(() => {
        sendGa('unmount');
        onClose();
    }, [onClose]);

    const handleOpenUnmountDialog = useCallback(() => {
        dispatch(unmountRequest({ item, onUnmount }));
    }, [dispatch, item, onUnmount]);

    const renderAccessRightsDropdown = useCallback(
        (onClick): ReactElement | null => {
            const values = {
                ...accessRightsOptions,
                [EAccessRights.DELETE_USER]: DELETE_USER,
            };

            const list = Object.values(values).map((item: IAccessRightsOptions) => ({
                id: item.key,
                text: item.dropdownText,
                check: item.key === user?.accessRights,
                qaValue: item.key,
            }));

            return <DropdownMenu list={list} theme="octavius" onClick={onClick} className={classNames(styles.dropdownMenu)} />;
        },
        [accessRightsOptions, user?.accessRights]
    );

    const desktopDropdownColor = useDesktopDropdownColor(variant);

    if (isOwner) {
        return <div className={styles.owner}>Владелец папки</div>;
    }

    if (isMounted) {
        return (
            <div className={styles.unmount} onClick={handleOpenUnmountDialog} data-qa-value={UNMOUNT_USER.key}>
                <div className={styles.unmountText}>{UNMOUNT_USER.text}</div>
            </div>
        );
    }

    return (
        <div className={styles.dropdown}>
            <Dropdown
                withinDialog
                theme="octavius"
                value={variant === SHARING_VARIANT.folders ? userRight?.text.toLowerCase() : userRight?.text}
                qaValue={userRight?.key}
                as={SharingDropdown}
                color={isPhone ? EDropdownColors.BLUE : desktopDropdownColor}
                placement={tooltipPlacements.BOTTOM_LEFT}
                content={renderAccessRightsDropdown}
                onClickItem={handleAccessRightsUpdate}
                tooltipOffset={TOOLTIP_OFFSET}
                animatedTooltip
                isPhone={isPhone}
                isUserDropdown
                redesign={variant !== SHARING_VARIANT.default}
                isTabs={variant === SHARING_VARIANT.folders}
                tooltipClassName={isPhone ? styles.tooltip : undefined}
                noPageOffset
            />
        </div>
    );
};

// eslint-disable-next-line max-lines-per-function
export const useWeblinkAccessRights = ({
    user,
    item,
    isPhone,
    isOwner,
    isReadOnly,
    variant,
    hideUserBlock,
}): ReactElement | null | string => {
    const dispatch = useDispatch();

    const isBizUser = useSelector(UserSelectors.isBizUser);
    const itemStorage = useSelector(getCurrentStorage);

    const rightsList = useMemo(() => {
        if (variant === SHARING_VARIANT.foldersBiz) {
            return PUBLIC_ACCESS_RIGHTS_OPTIONS_FOLDER_BIZ;
        }
        return getPublicAccessRightsOptions();
    }, [variant]);

    const userRight = rightsList[user.accessRights];

    const onActionClick = useCallback(() => {
        if (!user || !item) {
            return;
        }

        const xrayUserType = isBizUser ? 'biz' : 'b2c';

        let xrayItemType = XrayItemType.file;
        if (itemStorage === EStorageType.albums) {
            xrayItemType = XrayItemType.album;
        } else if (item.isFolder) {
            xrayItemType = XrayItemType.folder;
        }
        sendXray([AB_FEATURE_NAME, 'wblnk', 'usr', 'dlt', xrayUserType, xrayItemType]);

        dispatch(weblinkDeleteEmailAccess({ email: user.email, weblink: item?.weblink }));
    }, [dispatch, isBizUser, item, itemStorage, user]);

    const handleAccessRightsUpdate = useCallback(
        (accessRight): void => {
            if (!user) {
                return;
            }

            if (accessRight === DELETE_USER.key) {
                const domainAccess = ('weblinkDomainAccess' in item && item.weblinkDomainAccess) || DomainAccess.EXTERNAL;

                renderDeletePublicAccessDialog({ domainAccess, onActionClick, isFolder: item?.isFolder });
                return;
            }

            sendXray([AB_FEATURE_NAME, 'wblnk', 'usr', 'rghts', 'chng'], {
                [accessRight]: 1,
            });
            // update on personal
            dispatch(weblinkSetEmailAccess({ email: user.email, accessRight, weblink: item?.weblink }));
        },
        [dispatch, item?.weblink, item?.weblinkDomainAccess, item?.isFolder, onActionClick, user]
    );

    const renderAccessRightsDropdown = useCallback(
        (onClick): ReactElement | null => {
            const values = isReadOnly
                ? {
                      [EAccessRights.COMMENT]: rightsList[EAccessRights.COMMENT],
                      [EPublicAccessRights.READ_ONLY]: rightsList[EPublicAccessRights.READ_ONLY],
                      [EAccessRights.DELETE_USER]: DELETE_USER,
                  }
                : {
                      ...rightsList,
                      [EAccessRights.DELETE_USER]: DELETE_USER,
                  };

            const list = Object.values(values).map((item) => ({
                id: item.key,
                text: item.dropdownText,
                check: item.key === user?.accessRights,
                qaValue: item.key,
            }));

            return <DropdownMenu list={list} theme="octavius" onClick={onClick} className={classNames(styles.dropdownMenu)} />;
        },
        [isReadOnly, rightsList, user?.accessRights]
    );

    const desktopDropdownColor = useDesktopDropdownColor(variant);

    if (hideUserBlock) {
        return <></>;
    }

    if (isOwner) {
        return <div className={styles.owner}>владелец</div>;
    }

    return (
        <div className={styles.dropdown}>
            <Dropdown
                withinDialog
                theme="octavius"
                value={
                    variant === SHARING_VARIANT.folders || variant === SHARING_VARIANT.foldersBiz
                        ? userRight?.text.toLowerCase()
                        : userRight?.text
                }
                qaValue={userRight?.key}
                as={SharingDropdown}
                color={isPhone ? EDropdownColors.BLUE : desktopDropdownColor}
                placement={tooltipPlacements.BOTTOM_LEFT}
                content={renderAccessRightsDropdown}
                onClickItem={handleAccessRightsUpdate}
                tooltipOffset={TOOLTIP_OFFSET}
                animatedTooltip
                isPhone={isPhone}
                isUserDropdown
                redesign={variant !== SHARING_VARIANT.default}
                tooltipClassName={isPhone ? styles.tooltip : undefined}
            />
        </div>
    );
};
